import { resolveStrings } from './i18n.js';
import { ALL_TEETH, formatTooth, isFdi, neighbour, opposing } from './numbering.js';
import { DEFAULT_THEME, mergeTheme, readThemeFromCss } from './theme.js';
const STYLE_ID = 'dental-3d-styles';
const CSS = `
.dental-3d{position:relative;display:block;width:100%;height:100%;min-height:120px;box-sizing:border-box}
.dental-3d__stage{position:absolute;inset:0;overflow:hidden;border-radius:inherit}
.dental-3d__stage:focus-visible{outline:2px solid var(--dental-selected,#2f7bf5);outline-offset:-2px}
.dental-3d__tooltip{position:absolute;pointer-events:none;z-index:2;padding:.3em .55em;border-radius:.4em;
  font:500 12px/1.3 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;color:var(--dental-tooltip-text,#fff);
  background:var(--dental-tooltip-bg,rgba(15,23,42,.9));white-space:nowrap;transform:translate(-50%,calc(-100% - 12px));
  opacity:0;transition:opacity .12s}
.dental-3d__tooltip[data-visible]{opacity:1}
.dental-3d__status{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;pointer-events:none;
  font:13px system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;color:var(--dental-status-text,#64748b);text-align:center;padding:1em}
.dental-3d[data-state="ready"] .dental-3d__status{display:none}
.dental-3d__a11y{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);
  clip-path:inset(50%);white-space:nowrap;border:0;list-style:none}
`;
function ensureStyles(doc) {
    if (doc.getElementById(STYLE_ID))
        return;
    const style = doc.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    doc.head.appendChild(style);
}
/**
 * Framework-free interactive 3D dental chart.
 *
 * ```ts
 * const viewer = new DentalViewer({ container: document.querySelector('#chart')! });
 * viewer.on('select', (e) => console.log(e.fdi));
 * await viewer.ready;
 * viewer.setTeeth({ '36': { status: 'filling', badge: 3 } });
 * ```
 *
 * Call `dispose()` when the container is removed.
 */
export class DentalViewer {
    options;
    /** Resolves once the model is loaded and the first frame can be rendered. */
    ready;
    engine = null;
    state_ = 'loading';
    root;
    stage;
    tooltip;
    status;
    a11yList;
    a11yButtons = new Map();
    handlers = {
        select: new Set(),
        hover: new Set(),
        ready: new Set(),
        error: new Set(),
    };
    teeth = {};
    selected_ = null;
    hovered_ = null;
    view_ = 'front';
    jaw_ = 'both';
    open_ = false;
    numbering_ = 'fdi';
    labels_ = false;
    missingMode_ = 'ghost';
    interaction_ = {};
    strings;
    explicitTheme;
    theme_;
    useCssVars;
    themeObserver = null;
    mediaQuery = null;
    themeRefreshQueued = false;
    loadedTeeth = [];
    disposed = false;
    constructor(options) {
        this.options = options;
        const container = options.container;
        if (!container)
            throw new Error('DentalViewer: options.container is required');
        const doc = container.ownerDocument;
        ensureStyles(doc);
        this.root = container;
        this.root.classList.add('dental-3d');
        this.root.dataset['state'] = 'loading';
        this.stage = doc.createElement('div');
        this.stage.className = 'dental-3d__stage';
        this.stage.tabIndex = 0;
        this.stage.setAttribute('role', 'application');
        this.tooltip = doc.createElement('div');
        this.tooltip.className = 'dental-3d__tooltip';
        this.tooltip.setAttribute('role', 'tooltip');
        this.status = doc.createElement('div');
        this.status.className = 'dental-3d__status';
        this.status.setAttribute('aria-live', 'polite');
        this.a11yList = doc.createElement('ul');
        this.a11yList.className = 'dental-3d__a11y';
        this.root.append(this.stage, this.tooltip, this.status, this.a11yList);
        this.strings = resolveStrings(options.locale);
        this.stage.setAttribute('aria-label', this.strings.teethList);
        this.a11yList.setAttribute('aria-label', this.strings.teethList);
        this.status.textContent = this.strings.loading;
        this.numbering_ = options.numbering ?? 'fdi';
        this.labels_ = options.labels ?? false;
        this.missingMode_ = options.missingMode ?? 'ghost';
        this.interaction_ = options.interaction ?? {};
        this.teeth = options.teeth ?? {};
        this.view_ = options.view ?? 'front';
        this.explicitTheme = options.theme;
        this.useCssVars = options.cssVariables ?? true;
        this.theme_ = this.computeTheme();
        this.stage.addEventListener('keydown', this.onKeyDown);
        this.stage.addEventListener('pointermove', this.onStagePointerMove);
        this.a11yList.addEventListener('click', this.onA11yClick);
        if (this.useCssVars)
            this.observeTheme(doc);
        this.ready = this.boot();
        // avoid unhandled rejection noise: errors are reported through the 'error' event
        this.ready.catch(() => undefined);
    }
    // -------------------------------------------------------------------------------------
    // public API
    get state() {
        return this.state_;
    }
    get selected() {
        return this.selected_;
    }
    get hovered() {
        return this.hovered_;
    }
    get theme() {
        return this.theme_;
    }
    /** FDI codes present in the loaded model (empty until ready). */
    get availableTeeth() {
        return this.loadedTeeth;
    }
    /** Replaces all tooth states (diffed internally). */
    setTeeth(states) {
        this.teeth = { ...states };
        this.engine?.setTeeth(this.teeth, this.missingMode_);
        this.refreshA11y();
    }
    setTooth(fdi, state) {
        const next = { ...this.teeth };
        if (state === null)
            delete next[fdi];
        else
            next[fdi] = state;
        this.setTeeth(next);
    }
    getTooth(fdi) {
        return this.teeth[fdi];
    }
    /** Programmatic selection; `focus` flies the camera to the tooth. */
    select(fdi, opts = {}) {
        this.applySelection(fdi, 'api', null, opts.focus ?? false);
    }
    setView(view) {
        this.view_ = view;
        this.engine?.setView(view, true);
    }
    setJaw(jaw) {
        this.jaw_ = jaw;
        this.engine?.setJaw(jaw);
    }
    get jaw() {
        return this.jaw_;
    }
    setOpen(open) {
        this.open_ = open;
        this.engine?.setOpen(open, true);
    }
    get open() {
        return this.open_;
    }
    setNumbering(numbering) {
        this.numbering_ = numbering;
        this.engine?.setLabels(this.labelOptions());
        this.refreshA11y();
        if (this.hovered_)
            this.showTooltip(this.hovered_);
    }
    get numbering() {
        return this.numbering_;
    }
    setLabels(labels) {
        this.labels_ = labels;
        this.engine?.setLabels(this.labelOptions());
    }
    get labels() {
        return this.labels_;
    }
    setMissingMode(mode) {
        this.missingMode_ = mode;
        this.engine?.setTeeth(this.teeth, mode);
    }
    setTheme(theme) {
        this.explicitTheme = theme;
        this.refreshTheme();
    }
    /**
     * Re-reads the `--dental-*` CSS variables. Called automatically when the document, body or
     * container change class/style attributes; call it yourself after changing variables on
     * another ancestor or through a stylesheet swap.
     */
    refreshTheme() {
        this.theme_ = this.computeTheme();
        this.engine?.setTheme(this.theme_);
    }
    setInteraction(opts) {
        this.interaction_ = opts;
        this.engine?.setInteraction(opts);
    }
    setLocale(locale) {
        this.strings = resolveStrings(locale);
        this.stage.setAttribute('aria-label', this.strings.teethList);
        this.a11yList.setAttribute('aria-label', this.strings.teethList);
        if (this.state_ === 'loading')
            this.status.textContent = this.strings.loading;
        if (this.state_ === 'error')
            this.status.textContent = this.strings.loadError;
        this.refreshA11y();
    }
    /** Formats a tooth for display in the current numbering system. */
    format(fdi) {
        return formatTooth(fdi, this.numbering_);
    }
    on(event, handler) {
        const set = this.handlers[event];
        set.add(handler);
        return () => {
            set.delete(handler);
        };
    }
    /** Recomputes the canvas size; normally handled automatically by a ResizeObserver. */
    resize() {
        this.engine?.resize();
    }
    /** Frees GPU resources, observers and DOM. The viewer cannot be reused afterwards. */
    dispose() {
        if (this.disposed)
            return;
        this.disposed = true;
        this.state_ = 'disposed';
        this.engine?.dispose();
        this.engine = null;
        this.themeObserver?.disconnect();
        this.mediaQuery?.removeEventListener('change', this.onMediaChange);
        this.stage.removeEventListener('keydown', this.onKeyDown);
        this.stage.removeEventListener('pointermove', this.onStagePointerMove);
        this.a11yList.removeEventListener('click', this.onA11yClick);
        this.stage.remove();
        this.tooltip.remove();
        this.status.remove();
        this.a11yList.remove();
        this.root.classList.remove('dental-3d');
        delete this.root.dataset['state'];
        for (const set of Object.values(this.handlers))
            set.clear();
    }
    /** Internals exposed for tests and debugging; not part of the stable API. */
    get debug() {
        return {
            memory: () => this.engine?.memory() ?? { geometries: 0, textures: 0 },
            frames: () => this.engine?.frames ?? 0,
            pick: (x, y) => this.engine?.pick(x, y) ?? null,
        };
    }
    // -------------------------------------------------------------------------------------
    // boot
    async boot() {
        try {
            const { createEngine } = await import('./engine.js');
            if (this.disposed)
                return;
            const engine = createEngine({
                element: this.stage,
                onHover: (fdi) => this.applyHover(fdi),
                onPick: (fdi, pointer, double) => this.applySelection(fdi, 'pointer', pointer, double),
            });
            this.engine = engine;
            engine.setTheme(this.theme_);
            engine.setInteraction(this.interaction_);
            const source = this.options.modelUrl ?? (await loadDefaultModel());
            if (this.disposed) {
                engine.dispose();
                return;
            }
            this.loadedTeeth = await engine.load(source);
            if (this.disposed) {
                engine.dispose();
                return;
            }
            engine.setTeeth(this.teeth, this.missingMode_);
            engine.setLabels(this.labelOptions());
            engine.setJaw(this.jaw_);
            engine.setOpen(this.open_, false);
            engine.setView(this.view_, false);
            if (this.selected_)
                engine.setSelected(this.selected_, false);
            this.buildA11y();
            this.state_ = 'ready';
            this.root.dataset['state'] = 'ready';
            this.emit('ready', { type: 'ready', teeth: [...this.loadedTeeth] });
        }
        catch (error) {
            if (this.disposed)
                return;
            this.state_ = 'error';
            this.root.dataset['state'] = 'error';
            this.status.textContent = this.strings.loadError;
            this.emit('error', { type: 'error', error });
            throw error;
        }
    }
    labelOptions() {
        return { enabled: this.labels_, format: (fdi) => formatTooth(fdi, this.numbering_) };
    }
    emit(event, payload) {
        for (const h of this.handlers[event]) {
            try {
                h(payload);
            }
            catch (err) {
                console.error(err);
            }
        }
    }
    // -------------------------------------------------------------------------------------
    // selection & hover
    applySelection(fdi, source, pointer, focus) {
        if (fdi &&
            (this.teeth[fdi]?.disabled || (this.loadedTeeth.length && !this.loadedTeeth.includes(fdi)))) {
            return;
        }
        const changed = fdi !== this.selected_;
        this.selected_ = fdi;
        this.engine?.setSelected(fdi, focus);
        if (changed) {
            for (const [code, btn] of this.a11yButtons) {
                btn.setAttribute('aria-pressed', String(code === fdi));
            }
        }
        if (changed || focus || source === 'pointer') {
            this.emit('select', {
                type: 'select',
                fdi,
                state: fdi ? this.teeth[fdi] : undefined,
                pointer,
                source,
            });
        }
    }
    applyHover(fdi) {
        if (fdi && this.teeth[fdi]?.disabled)
            fdi = null;
        if (fdi === this.hovered_)
            return;
        this.hovered_ = fdi;
        this.engine?.setHover(fdi);
        if (fdi)
            this.showTooltip(fdi);
        else
            this.hideTooltip();
        this.emit('hover', { type: 'hover', fdi, state: fdi ? this.teeth[fdi] : undefined });
    }
    describe(fdi) {
        const status = this.teeth[fdi]?.status;
        const parts = [
            `${this.strings.tooth} ${formatTooth(fdi, this.numbering_)}`,
            this.strings.names[fdi],
        ];
        if (status && status !== 'present')
            parts.push(this.strings.statuses[status]);
        return parts.join(' · ');
    }
    showTooltip(fdi) {
        this.tooltip.textContent = this.describe(fdi);
        this.tooltip.dataset['visible'] = '';
    }
    hideTooltip() {
        delete this.tooltip.dataset['visible'];
    }
    onStagePointerMove = (ev) => {
        if (ev.pointerType === 'touch')
            return;
        const rect = this.root.getBoundingClientRect();
        this.tooltip.style.left = `${ev.clientX - rect.left}px`;
        this.tooltip.style.top = `${ev.clientY - rect.top}px`;
    };
    // -------------------------------------------------------------------------------------
    // keyboard
    onKeyDown = (ev) => {
        if (ev.defaultPrevented || ev.altKey || ev.ctrlKey || ev.metaKey)
            return;
        const current = this.hovered_ ?? this.selected_;
        const move = (next) => {
            if (!next)
                return;
            // skip disabled / absent teeth
            let candidate = next;
            let guard = 0;
            while (candidate &&
                (this.teeth[candidate]?.disabled || !this.isLoaded(candidate)) &&
                guard++ < 16) {
                const dir = ev.key === 'ArrowLeft' ? -1 : 1;
                candidate = neighbour(candidate, dir);
            }
            if (candidate)
                this.applyHover(candidate);
        };
        switch (ev.key) {
            case 'ArrowLeft':
            case 'ArrowRight': {
                ev.preventDefault();
                const dir = ev.key === 'ArrowLeft' ? -1 : 1;
                if (!current) {
                    move(dir === 1 ? '11' : '21');
                }
                else {
                    move(neighbour(current, dir));
                }
                break;
            }
            case 'ArrowUp':
            case 'ArrowDown': {
                ev.preventDefault();
                if (!current)
                    move(ev.key === 'ArrowUp' ? '11' : '41');
                else
                    move(opposing(current));
                break;
            }
            case 'Enter':
            case ' ': {
                if (!this.hovered_)
                    return;
                ev.preventDefault();
                this.applySelection(this.hovered_, 'keyboard', null, false);
                break;
            }
            case 'Escape': {
                ev.preventDefault();
                if (this.selected_)
                    this.applySelection(null, 'keyboard', null, false);
                else
                    this.applyHover(null);
                break;
            }
            case '1':
            case '2':
            case '3':
            case '4':
            case '5': {
                ev.preventDefault();
                const views = ['front', 'left', 'right', 'upper-occlusal', 'lower-occlusal'];
                this.setView(views[Number(ev.key) - 1]);
                break;
            }
            default:
                return;
        }
    };
    isLoaded(fdi) {
        return this.loadedTeeth.length === 0 || this.loadedTeeth.includes(fdi);
    }
    // -------------------------------------------------------------------------------------
    // accessibility list
    buildA11y() {
        this.a11yList.textContent = '';
        this.a11yButtons.clear();
        const doc = this.root.ownerDocument;
        const codes = this.loadedTeeth.length ? this.loadedTeeth : ALL_TEETH;
        for (const fdi of codes) {
            const li = doc.createElement('li');
            const btn = doc.createElement('button');
            btn.type = 'button';
            btn.dataset['fdi'] = fdi;
            li.appendChild(btn);
            this.a11yList.appendChild(li);
            this.a11yButtons.set(fdi, btn);
        }
        this.refreshA11y();
    }
    refreshA11y() {
        for (const [fdi, btn] of this.a11yButtons) {
            btn.textContent = this.describe(fdi);
            btn.setAttribute('aria-pressed', String(fdi === this.selected_));
            btn.disabled = Boolean(this.teeth[fdi]?.disabled);
        }
    }
    onA11yClick = (ev) => {
        const btn = ev.target.closest('button');
        const fdi = btn?.dataset['fdi'];
        if (!isFdi(fdi))
            return;
        this.applySelection(this.selected_ === fdi ? null : fdi, 'keyboard', null, false);
    };
    // -------------------------------------------------------------------------------------
    // theme
    computeTheme() {
        const fromCss = this.useCssVars ? readThemeFromCss(this.root) : undefined;
        return mergeTheme(DEFAULT_THEME, fromCss, this.explicitTheme);
    }
    observeTheme(doc) {
        if (typeof MutationObserver === 'function') {
            this.themeObserver = new MutationObserver(() => this.queueThemeRefresh());
            const opts = {
                attributes: true,
                attributeFilter: ['class', 'style', 'data-theme', 'data-mode', 'color-scheme'],
            };
            this.themeObserver.observe(doc.documentElement, opts);
            if (doc.body)
                this.themeObserver.observe(doc.body, opts);
            // variables are often set on the container itself (or toggled via its class)
            this.themeObserver.observe(this.root, opts);
            if (this.root.parentElement && this.root.parentElement !== doc.body) {
                this.themeObserver.observe(this.root.parentElement, opts);
            }
        }
        if (typeof matchMedia === 'function') {
            this.mediaQuery = matchMedia('(prefers-color-scheme: dark)');
            this.mediaQuery.addEventListener('change', this.onMediaChange);
        }
    }
    onMediaChange = () => this.queueThemeRefresh();
    queueThemeRefresh() {
        if (this.themeRefreshQueued || this.disposed)
            return;
        this.themeRefreshQueued = true;
        // a macrotask: lets the style recalculation settle and coalesces bursts of mutations
        setTimeout(() => {
            this.themeRefreshQueued = false;
            if (this.disposed)
                return;
            const next = this.computeTheme();
            if (JSON.stringify(next) !== JSON.stringify(this.theme_)) {
                this.theme_ = next;
                this.engine?.setTheme(next);
            }
        }, 0);
    }
}
/** Decodes the bundled default model (lazy chunk) into an ArrayBuffer. */
export async function loadDefaultModel() {
    const { DEFAULT_MODEL_BASE64 } = await import('./model/default-model.generated.js');
    return base64ToArrayBuffer(DEFAULT_MODEL_BASE64);
}
function base64ToArrayBuffer(b64) {
    if (typeof atob === 'function') {
        const bin = atob(b64);
        const out = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++)
            out[i] = bin.charCodeAt(i);
        return out.buffer;
    }
    // Node without atob (very old) – Buffer fallback
    const buf = globalThis.Buffer.from(b64, 'base64');
    return buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.byteLength);
}
//# sourceMappingURL=viewer.js.map